import { useCallback, useState } from 'react';
import { HttpError, type StartSmsTestBody } from '@vagusx/clients';
import { getSmsTestClient } from '@/lib/api-clients';

export interface SmsChatMessage {
  role: 'user' | 'assistant';
  content: string;
}

interface UseSmsChatResult {
  messages: SmsChatMessage[];
  conversationId: string | null;
  isLoading: boolean;
  /** True once the agent has ended the conversation (booked, closed, or opted out). */
  done: boolean;
  /** Set when Start fails (reply errors surface as an assistant message instead). */
  startError: string | null;
  /** Whether a conversation has been started (drives setup → chat transition). */
  started: boolean;
  start: (body: StartSmsTestBody) => Promise<void>;
  sendMessage: (text: string) => Promise<void>;
  reset: () => void;
}

/**
 * Drives the SMS-agent test tool: Start opens a real outbound conversation for
 * the current tenant (via `/api/sms-test/start` → apps/sms-agent), and each
 * sendMessage feeds one patient reply (`/api/sms-test/reply`). Plain useState
 * (like use-scheduling-chat) — the global mutation-error toast doesn't apply, so
 * Start errors go to `startError` and reply errors become an assistant message.
 */
export function useSmsChat(): UseSmsChatResult {
  const [messages, setMessages] = useState<SmsChatMessage[]>([]);
  const [conversationId, setConversationId] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [done, setDone] = useState(false);
  const [startError, setStartError] = useState<string | null>(null);

  const start = useCallback(async (body: StartSmsTestBody): Promise<void> => {
    setIsLoading(true);
    setStartError(null);
    try {
      const data = await getSmsTestClient().start(body);
      setConversationId(data.conversationId);
      setMessages(data.messages.map((content) => ({ role: 'assistant', content })));
      setDone(data.done);
    } catch (err) {
      setStartError(err instanceof HttpError ? err.message : 'Failed to start the conversation.');
    } finally {
      setIsLoading(false);
    }
  }, []);

  const sendMessage = useCallback(
    async (text: string): Promise<void> => {
      if (!conversationId || !text.trim() || isLoading || done) return;
      setMessages((prev) => [...prev, { role: 'user', content: text }]);
      setIsLoading(true);
      try {
        const data = await getSmsTestClient().reply({ conversationId, body: text });
        setMessages((prev) => [
          ...prev,
          ...data.messages.map((content): SmsChatMessage => ({ role: 'assistant', content })),
        ]);
        setDone(data.done);
      } catch {
        setMessages((prev) => [
          ...prev,
          { role: 'assistant', content: 'Sorry, something went wrong. Please try again.' },
        ]);
      } finally {
        setIsLoading(false);
      }
    },
    [conversationId, isLoading, done]
  );

  const reset = useCallback((): void => {
    setMessages([]);
    setConversationId(null);
    setDone(false);
    setStartError(null);
  }, []);

  return {
    messages,
    conversationId,
    isLoading,
    done,
    startError,
    started: conversationId !== null,
    start,
    sendMessage,
    reset,
  };
}
